/**
 * Analytics Portal V1 — traffic sources (Phase 10, plan §2.8 + §12.2).
 *
 * Renders the API's `traffic_sources[]` (`{value, label, visits}`) in the server's order with
 * `Unknown` forced last. Bar widths are a visual scale against the largest row only — no share,
 * percentage or total is computed or claimed.
 */
import React from "react";
import { ChartCard } from "./ChartCard";
import { EmptyState } from "./EmptyState";

export const SOURCE_LABEL = {
  so_search: "Startup Otter Search", directory: "Directory", chatter: "Chatter",
  shared_post: "Shared Post", personal_profile: "Personal Profile",
  business_profile: "Business Profile", capital_profile: "Capital Profile",
  event: "Event", resource: "Resource", notification: "Notification",
  direct_url: "Direct URL", external_referral: "External Referral", unknown: "Unknown",
};

export const TrafficSourceChart = ({ sources = [], onChangeRange }) => {
  const rows = [...sources].sort((a, b) => (a.value === "unknown") - (b.value === "unknown"));
  const max = rows.reduce((top, row) => Math.max(top, row.visits || 0), 0);

  return (
    <ChartCard title="Traffic sources" description="Where visits came from. Never inferred."
               testId="analytics-traffic-sources"
               summary={rows.length
                 ? rows.map((row) => `${SOURCE_LABEL[row.value] || row.label}: ${row.visits} visits`).join("; ")
                 : undefined}>
      {!rows.length ? (
        <EmptyState state="no-data-in-period" onAction={onChangeRange} />
      ) : (
        <ul className="space-y-2.5">
          {rows.map((row) => (
            <li key={row.value} data-testid={`analytics-traffic-source-${row.value}`}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-foreground">
                  {SOURCE_LABEL[row.value] || row.label || row.value}
                </span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {row.visits?.toLocaleString?.() ?? row.visits}
                </span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-accent">
                <div className="h-full rounded-full bg-primary/80"
                     style={{ width: `${max ? Math.max(2, Math.round((row.visits / max) * 100)) : 2}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </ChartCard>
  );
};
